import { useState, useEffect } from 'react'

const FILTERS = [
  { key: null, label: 'All' },
  { key: 'photo', label: 'Progress' },
  { key: 'meal', label: 'Meals' },
  { key: 'chat', label: 'Chat' },
]

const TYPE_BADGE_COLORS = {
  photo: 'bg-lume-teal/80 text-dial-black',
  meal: 'bg-amber-500/80 text-white',
  chat: 'bg-status-info/80 text-white',
}

function Lightbox({ item, onClose }) {
  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);
  return (
    <div role="dialog" aria-modal="true" aria-label={item.caption || "Image lightbox"} className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={onClose}>
      <div className="relative max-w-[90vw] max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white/80 hover:text-white p-2"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <img
          src={item.url}
          alt={item.caption || ''}
          className="max-w-full max-h-[80vh] object-contain rounded-lg"
        />
        <div className="mt-2 text-center">
          {item.caption && (
            <p className="text-sm text-white/90">{item.caption}</p>
          )}
          {item.date && (
            <p className="text-xs text-white/60 mt-1">
              {new Date(item.date).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' })}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default function MediaGallery({ media, isLoading }) {
  const [filter, setFilter] = useState(null)
  const [lightboxItem, setLightboxItem] = useState(null)

  const filtered = filter
    ? media?.filter(item => item.sourceType === filter)
    : media

  if (isLoading) {
    return (
      <div>
        <div className="flex gap-2 mb-3">
          {FILTERS.map(f => (
            <div key={f.label} className="h-8 w-16 bg-dial-elevated rounded-full animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="aspect-square bg-dial-elevated rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Filter tabs */}
      <div className="flex gap-2 mb-3 overflow-x-auto">
        {FILTERS.map(f => (
          <button
            key={f.label}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
              filter === f.key
                ? 'bg-lume-teal text-dial-black'
                : 'bg-dial-elevated text-indices-medium hover:text-indices-bright border border-dial-border'
            }`}
          >
            {f.label}
            {f.key && media ? ` (${media.filter(item => item.sourceType === f.key).length})` : ''}
          </button>
        ))}
      </div>

      {/* Grid */}
      {!filtered || filtered.length === 0 ? (
        <div className="text-center py-8 text-indices-muted text-sm">
          No media found
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {filtered.map(item => (
            <button
              key={item.id}
              onClick={() => setLightboxItem(item)}
              className="relative aspect-square rounded-lg overflow-hidden bg-dial-elevated group"
            >
              <img
                src={item.thumbnailUrl || item.url}
                alt={item.caption || ''}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                loading="lazy"
              />
              {/* Type badge */}
              <span className={`absolute top-1.5 left-1.5 px-1.5 py-0.5 text-[10px] font-medium rounded ${TYPE_BADGE_COLORS[item.sourceType] || 'bg-dial-elevated text-indices-medium'}`}>
                {item.sourceType === 'photo' ? 'Progress' : item.sourceType === 'meal' ? 'Meal' : 'Chat'}
              </span>
              {/* Date overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1.5">
                <p className="text-[10px] text-white/80 truncate">
                  {item.date ? new Date(item.date).toLocaleDateString([], { month: 'short', day: 'numeric' }) : ''}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxItem && (
        <Lightbox item={lightboxItem} onClose={() => setLightboxItem(null)} />
      )}
    </div>
  )
}
